import { addSheetBackLink, PARENT_SHEETS } from "../sheetNavigation.js";
import {
  BACK_ROW,
  BODY_FONT,
  HEADER_FONT_11,
  HEADER_ROW,
  NA_FILL,
  VALUE_FILL,
  hideColumnsAfter,
  setCell,
} from "./detailSheetCommon.js";
import {
  COL_D,
  COL_E,
  COL_F,
  COL_G,
  COL_H,
  COL_I,
  COL_J,
  COL_K,
  addSheetTitle,
  applyBorderPattern,
  outerRowPatterns,
  setupPrinciple2DetailSheet,
  writeDataRow,
} from "./principle2DetailHelpers.js";

export const SHEET_NAME = "Stakeholder group";

const SHEET_TITLE =
  "List stakeholder groups identified as key for your entity and the frequency of engagement with each stakeholder group.";

const WIDTHS = {
  A: 9.1796875,
  B: 9.1796875,
  C: 9.1796875,
  D: 9.1796875,
  E: 30.7265625,
  F: 30.7265625,
  G: 30.7265625,
  H: 30.7265625,
  I: 30.7265625,
  J: 30.7265625,
  K: 46,
  L: 9.1796875,
  M: 9.1796875,
  N: 9.1796875,
};

const TEMPLATE_ROW = 10;
const SPACER_ROW = 11;
const DATA_START_ROW = 12;
const COL_END = COL_K;

const HEADER_BORDERS = Array(8).fill("tttt");
const TEMPLATE_BORDERS = HEADER_BORDERS;
const DATA_BORDERS = HEADER_BORDERS;
const NA_COLS = [COL_H, COL_J];

const HEADERS = [
  { col: COL_D, label: "Sr. No." },
  { col: COL_E, label: "Stakeholder Group" },
  { col: COL_F, label: "Whether identified as Vulnerable & Marginalized Group" },
  { col: COL_G, label: "Channels of communication" },
  { col: COL_H, label: "Details of Other Channels of communication" },
  { col: COL_I, label: "Frequency of engagement" },
  { col: COL_J, label: "Details of Other Frequency of engagement" },
  {
    col: COL_K,
    label:
      "Purpose and scope of engagement including key topics and concerns raised during such engagement",
  },
];

const STAKEHOLDER_GROUPS = [
  ["Investors", "investors"],
  ["Shareholders", "shareholders"],
  ["Customers", "customers"],
  ["Business Partners", "businessPartners"],
  ["Employees & Workers", "employeesAndWorkers"],
  ["Regulatory Bodies", "regulatoryBodies"],
  ["Trade bodies & other organizations", "tradeBodies"],
  ["Local community", "localCommunity"],
];

function safeText(value, fallback = "") {
  if (value === null || value === undefined || value === "") return fallback;
  return String(value);
}

function hasStakeholderData(item) {
  if (!item || typeof item !== "object") return false;
  return Object.values(item).some(
    (value) => value !== null && value !== undefined && String(value).trim() !== ""
  );
}

function resolveRows(stakeholders) {
  const filled = STAKEHOLDER_GROUPS.filter(([, key]) => hasStakeholderData(stakeholders?.[key]));
  return filled.length ? filled : STAKEHOLDER_GROUPS;
}

export function buildStakeholderGroupSheet(workbook, sectionC) {
  const ws = workbook.addWorksheet(SHEET_NAME, { views: [{ showGridLines: false }] });

  setupPrinciple2DetailSheet(ws, {
    widths: WIDTHS,
    colEnd: COL_END,
    ySplit: 11,
    topLeftCell: "D12",
    hiddenCols: ["A", "B"],
  });
  hideColumnsAfter(ws, 14);

  addSheetBackLink(ws, PARENT_SHEETS.PRINCIPLE_4, {
    row: BACK_ROW,
    col: COL_END,
    mergeEnd: COL_END,
    align: "right",
    parentCell: "D8",
  });

  addSheetTitle(ws, COL_END, SHEET_TITLE, {
    borderPatterns: outerRowPatterns(COL_D, COL_END),
  });

  applyBorderPattern(ws, HEADER_ROW, COL_D, HEADER_BORDERS);
  ws.getRow(HEADER_ROW).height = 30;
  HEADERS.forEach(({ col, label }) => {
    setCell(ws, HEADER_ROW, col, label, {
      font: HEADER_FONT_11,
      bordered: false,
      align: "center",
    });
  });

  applyBorderPattern(ws, TEMPLATE_ROW, COL_D, TEMPLATE_BORDERS);
  for (let col = COL_D; col <= COL_END; col += 1) {
    setCell(ws, TEMPLATE_ROW, col, "", {
      bordered: false,
      fill: NA_COLS.includes(col) ? NA_FILL : undefined,
      font: BODY_FONT,
    });
  }
  ws.getRow(TEMPLATE_ROW).height = 30;
  ws.getRow(TEMPLATE_ROW).hidden = true;

  applyBorderPattern(ws, SPACER_ROW, COL_D, outerRowPatterns(COL_D, COL_END));
  for (let col = COL_D; col <= COL_END; col += 1) {
    const cell = ws.getCell(SPACER_ROW, col);
    if (!cell.font) cell.font = BODY_FONT;
  }
  ws.getRow(SPACER_ROW).height = 30;

  const stakeholders =
    sectionC?.principle4?.essentialIndicators?.identificationOfStakeholders || {};
  const rows = resolveRows(stakeholders);

  rows.forEach(([label, key], idx) => {
    const row = DATA_START_ROW + idx;
    const item = stakeholders?.[key] || {};
    const purpose = safeText(item?.purpose);

    writeDataRow(
      ws,
      row,
      COL_D,
      COL_END,
      [
        {
          col: COL_D,
          value: idx + 1,
          align: "right",
          fill: null,
          font: BODY_FONT,
          vertical: "top",
        },
        {
          col: COL_E,
          value: label,
          align: "left",
          fill: VALUE_FILL,
          vertical: "top",
        },
        {
          col: COL_F,
          value: safeText(item?.wheatherIdentified),
          align: "center",
          fill: VALUE_FILL,
          vertical: "top",
        },
        {
          col: COL_G,
          value: safeText(item?.channelOfCommunication),
          align: "center",
          fill: VALUE_FILL,
          vertical: "top",
        },
        {
          col: COL_H,
          value: safeText(item?.newChannelOfCommunication),
          align: "left",
          fill: NA_FILL,
          vertical: "top",
        },
        {
          col: COL_I,
          value: safeText(item?.frequency),
          align: "center",
          fill: VALUE_FILL,
          vertical: "top",
        },
        {
          col: COL_J,
          value: safeText(item?.newFrequency),
          align: "left",
          fill: NA_FILL,
          vertical: "top",
        },
        {
          col: COL_K,
          value: purpose,
          align: "left",
          fill: VALUE_FILL,
          vertical: "top",
        },
      ],
      { patterns: DATA_BORDERS }
    );

    ws.getCell(row, COL_D).alignment = {
      horizontal: "right",
      vertical: "top",
      wrapText: true,
    };
    for (let col = COL_E; col <= COL_END; col += 1) {
      const cell = ws.getCell(row, col);
      cell.alignment = {
        horizontal: col === COL_E || col === COL_H || col === COL_J || col === COL_K ? "left" : "center",
        vertical: "top",
        wrapText: true,
      };
    }
    ws.getRow(row).height = Math.max(30, Math.ceil(Math.max(purpose.length, 1) / 55) * 15);
  });

  return ws;
}
